import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  MyWarning, StaffFile, StaffNote, StaffNoteInput, StaffNoteRevision, StaffRecordFile,
  StaffWarning, StaffWarningInput,
} from '../types/staffRecords';

/**
 * Staff file notes and warnings (2026-10-19), `/api/staff-records`.
 * HR's half needs the Timesheet HR seat; `me` is the employee's own warnings.
 */
const BASE = '/staff-records';

/** The data, or the server's sentence as a thrown error. */
async function unwrap<T>(p: Promise<ApiResponse<T>>): Promise<T> {
  const res = await p;
  if (!res.success) throw new Error(res.error?.message || 'That did not work. Try again.');
  return res.data;
}

class StaffRecordService {
  // ----- HR -----
  getFile(userId: number): Promise<StaffFile> {
    return unwrap(apiService.get(`${BASE}/people/${userId}`));
  }

  addNote(userId: number, input: StaffNoteInput): Promise<StaffNote> {
    return unwrap(apiService.post(`${BASE}/people/${userId}/notes`, input));
  }

  editNote(noteId: number, input: Partial<StaffNoteInput>): Promise<StaffNote> {
    return unwrap(apiService.put(`${BASE}/notes/${noteId}`, input));
  }

  removeNote(noteId: number, reason: string): Promise<StaffNote> {
    return unwrap(apiService.post(`${BASE}/notes/${noteId}/remove`, { reason }));
  }

  noteRevisions(noteId: number): Promise<StaffNoteRevision[]> {
    return unwrap(apiService.get(`${BASE}/notes/${noteId}/revisions`));
  }

  addWarning(userId: number, input: StaffWarningInput): Promise<StaffWarning> {
    return unwrap(apiService.post(`${BASE}/people/${userId}/warnings`, input));
  }

  editWarning(warningId: number, input: Partial<StaffWarningInput>): Promise<StaffWarning> {
    return unwrap(apiService.put(`${BASE}/warnings/${warningId}`, input));
  }

  deleteDraft(warningId: number): Promise<{ id: number }> {
    return unwrap(apiService.delete(`${BASE}/warnings/${warningId}`));
  }

  issue(warningId: number, how: { send_in_app: boolean; given_in_person: boolean }): Promise<StaffWarning> {
    return unwrap(apiService.post(`${BASE}/warnings/${warningId}/issue`, how));
  }

  remind(warningId: number): Promise<StaffWarning> {
    return unwrap(apiService.post(`${BASE}/warnings/${warningId}/remind`, {}));
  }

  recordAcknowledgement(warningId: number, input: { name: string; response?: string | null }): Promise<StaffWarning> {
    return unwrap(apiService.post(`${BASE}/warnings/${warningId}/record-acknowledgement`, input));
  }

  withdraw(warningId: number, reason: string): Promise<StaffWarning> {
    return unwrap(apiService.post(`${BASE}/warnings/${warningId}/withdraw`, { reason }));
  }

  attach(kind: 'notes' | 'warnings', id: number, file: File): Promise<StaffRecordFile> {
    const form = new FormData();
    form.append('file', file);
    // A phone photo of a signed letter: well past the default 10 s.
    return unwrap(apiService.post(`${BASE}/${kind}/${id}/files`, form, undefined, { timeout: 120000 }));
  }

  /** For openDocument: relative to the API base. */
  filePath(fileId: number): string {
    return `${BASE}/files/${fileId}`;
  }

  removeFile(fileId: number): Promise<{ id: number }> {
    return unwrap(apiService.delete(`${BASE}/files/${fileId}`));
  }

  // ----- The employee -----
  myWarnings(): Promise<MyWarning[]> {
    return unwrap(apiService.get(`${BASE}/me/warnings`));
  }

  myWarningCount(): Promise<{ count: number }> {
    return unwrap(apiService.get(`${BASE}/me/warnings/count`));
  }

  myWarning(id: number): Promise<MyWarning> {
    return unwrap(apiService.get(`${BASE}/me/warnings/${id}`));
  }

  acknowledge(id: number, input: { hash: string; name: string; response?: string | null }): Promise<MyWarning> {
    return unwrap(apiService.post(`${BASE}/me/warnings/${id}/acknowledge`, input));
  }
}

export default new StaffRecordService();

/** The server's sentence from a thrown request, else a fallback. */
export function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e?.response?.data?.error?.message || e?.message || fallback;
}
